'use client';

import { Mic } from 'lucide-react';

import { memo, useEffect, useEffectEvent, useRef } from 'react';

import { cn } from '@/lib/utils';

import { useAudioLevel } from '../hooks/use-audio-level';

const BAR_MIN_HEIGHT = 3;
const BAR_MAX_EXTRA_HEIGHT = 15;

type AudioWaveIndicatorProps = {
	stream: MediaStream | null;
	showMicIcon?: boolean;
	/** Show "No audio detected" when the mic stays silent (used while recording) */
	showSilenceNudge?: boolean;
	/** Fires once per mount when the silence nudge first shows */
	onSilenceNudge?: () => void;
};

/**
 * Small pill with bars that follow the mic level, so candidates can see their audio is being picked up.
 */
export const AudioWaveIndicator = memo(function AudioWaveIndicator({
	stream,
	showMicIcon = true,
	showSilenceNudge = false,
	onSilenceNudge,
}: AudioWaveIndicatorProps) {
	const { bars, isSilent } = useAudioLevel(stream);
	const showNudge = showSilenceNudge && isSilent;

	const nudgeFiredRef = useRef(false);
	const fireSilenceNudge = useEffectEvent(() => onSilenceNudge?.());
	useEffect(() => {
		if (!showNudge || nudgeFiredRef.current) return;
		nudgeFiredRef.current = true;
		fireSilenceNudge();
	}, [showNudge]);

	return (
		<div
			role="status"
			aria-label={showNudge ? 'No audio detected' : 'Microphone level'}
			className={cn(
				'flex h-7 items-center gap-1.5 rounded-full backdrop-blur-sm transition-colors',
				// Waves alone get tighter padding so the pill reads as a circle
				showMicIcon || showNudge ? 'px-2.5' : 'px-[7px]',
				showNudge ? 'bg-red-100/90 text-red-700' : 'bg-white/70 text-primary'
			)}
		>
			{showMicIcon && <Mic className="h-3.5 w-3.5 shrink-0" />}
			<div
				aria-hidden
				className="flex h-[18px] items-center gap-[2px]"
			>
				{bars.map((bar, index) => (
					<span
						key={index}
						className="duration-[50ms] w-[2px] rounded-full bg-current transition-[height] ease-out"
						style={{ height: BAR_MIN_HEIGHT + bar * BAR_MAX_EXTRA_HEIGHT }}
					/>
				))}
			</div>
			{showNudge && (
				<span className="whitespace-nowrap text-xs font-medium">No audio detected</span>
			)}
		</div>
	);
});
